import { useState } from "react";
import { toast } from "sonner";
import { User, Mail, Building2, BadgeCheck, KeyRound } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { TopNav } from "@/components/TopNav";
import { useAuth } from "@/context/AuthContext";

export default function Profile() {
  const { user } = useAuth();
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success("Password updated successfully");
      setCur(""); setNext("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const Row = ({ icon: Icon, label, value }) => (
    <div className="flex items-center gap-3 py-3 border-b border-[#E4E4E7] last:border-0">
      <Icon size={18} className="text-gray-500" />
      <div className="flex-1">
        <div className="overline">{label}</div>
        <div className="text-sm mt-0.5">{value || "—"}</div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FFFFFF] text-[#0A0A0A]">
      <TopNav />
      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className="overline mb-2">Account</div>
        <h1 className="font-display font-black text-4xl tracking-tight mb-10">My Profile</h1>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-[#F5F5F5] border border-[#E4E4E7] rounded-sm p-6">
            <h2 className="font-display font-bold text-lg mb-4">Details</h2>
            <Row icon={User} label="Full Name" value={user?.name} />
            <Row icon={BadgeCheck} label="Employee ID" value={user?.emp_id} />
            <Row icon={Mail} label="Email" value={user?.email} />
            <Row icon={Building2} label="Department" value={user?.department} />
            <Row icon={BadgeCheck} label="Designation" value={user?.designation} />
          </div>

          <div className="bg-[#F5F5F5] border border-[#E4E4E7] rounded-sm p-6">
            <h2 className="font-display font-bold text-lg mb-4 flex items-center gap-2"><KeyRound size={18} className="text-[#0090A8]" /> Change Password</h2>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="overline block mb-2">Current Password</label>
                <input data-testid="current-password" type="password" value={cur} onChange={(e) => setCur(e.target.value)} required
                  className="w-full bg-[#FFFFFF] border border-[#E4E4E7] focus:border-[#0090A8] rounded-sm px-4 py-2.5 outline-none transition-colors" />
              </div>
              <div>
                <label className="overline block mb-2">New Password</label>
                <input data-testid="new-password" type="password" value={next} onChange={(e) => setNext(e.target.value)} required minLength={6}
                  className="w-full bg-[#FFFFFF] border border-[#E4E4E7] focus:border-[#0090A8] rounded-sm px-4 py-2.5 outline-none transition-colors" />
              </div>
              <button data-testid="change-password-submit" disabled={loading}
                className="w-full bg-[#0090A8] text-black hover:bg-[#00728A] font-bold rounded-sm py-2.5 transition-colors disabled:opacity-50">
                {loading ? "Updating…" : "Update Password"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
